import React, { useEffect, useState } from "react";
import PageLayout from "../../components/layouts/PageLayout";
import CustomNavbar from "../../components/common/CustomNavbar";

import {
  fetchBroadcastById,
  fetchBroadcastRecipients,
} from "../../features/message/Messageservice";

import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AppDispatch } from "../../store";

import { Broadcast, BroadcastRecipient } from "../../types/broadcastType";

import ECSLoader from "../../components/common/EcsLoader";
import { IoIosArrowRoundBack } from "react-icons/io";
import {
  FiUsers,
  FiAlertCircle,
  FiCheckCircle,
} from "react-icons/fi";
import { StatusShow } from "../../components/common/StausShow";
import Pagination from "../../components/common/Pagination";
import { FaIndianRupeeSign } from "react-icons/fa6";
import { StatusBadge } from "./BroadcastList";

const BroadcastDetails = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();

  const [broadcast, setBroadcast] = useState<Broadcast | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [recipients, setRecipients] = useState<BroadcastRecipient[]>([]);

  const [recipientLoading, setRecipientLoading] = useState(false);
  const [recipientError, setRecipientError] = useState("");

  const [recipientPage, setRecipientPage] = useState(0);
  const [recipientPageSize, setRecipientPageSize] = useState(10);
  const [recipientTotalPages, setRecipientTotalPages] = useState(0);
  const [recipientTotalItems, setRecipientTotalItems] = useState(0);

  useEffect(() => {
    const loadBroadcastDetails = async () => {
      if (!id) {
        setError("Broadcast ID is missing");
        return;
      }

      setLoading(true);
      setError("");

      try {
        const result = await dispatch(fetchBroadcastById(Number(id))).unwrap();

        setBroadcast(result);
      } catch (err: any) {
        setError(err?.message || err || "Failed to load broadcast details");
      } finally {
        setLoading(false);
      }
    };

    loadBroadcastDetails();
  }, [dispatch, id]);

  useEffect(() => {
    const loadRecipients = async () => {
      if (!id) return;

      setRecipientLoading(true);
      setRecipientError("");

      try {
        const result = await dispatch(
          fetchBroadcastRecipients({
            broadcastId: Number(id),
            page: recipientPage,
            size: recipientPageSize,
          }),
        ).unwrap();

        setRecipients(result.results || []);

        setRecipientTotalPages(result.page?.count || 0);
        setRecipientTotalItems(result.page?.total || 0);
      } catch (err: any) {
        setRecipientError(err?.message || err || "Failed to load recipients");

        setRecipients([]);
        setRecipientTotalPages(0);
        setRecipientTotalItems(0);
      } finally {
        setRecipientLoading(false);
      }
    };

    loadRecipients();
  }, [dispatch, id, recipientPage, recipientPageSize]);

  return (
    <PageLayout
      customNavbar={
        <CustomNavbar>
          <span className="font-bold text-lg uppercase">Broadcasts</span>
        </CustomNavbar>
      }
    >
      <ECSLoader show={loading} />

      <div className="space-y-6">
        <div className="flex flex-col gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="group flex w-fit items-center gap-1 text-sm font-medium text-text-secondary transition-colors hover:text-brand-main"
          >
            <IoIosArrowRoundBack
              size={26}
              className="transition-transform group-hover:-translate-x-1"
            />
            Back to Broadcasts
          </button>

          <div className="flex flex-col gap-3 rounded-xl border border-gray-100 bg-white px-5 py-5 shadow-sm sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="truncate text-xl font-bold text-brand-secondary sm:text-2xl">
                    {broadcast?.name || "Broadcast Details"}
                  </h1>

                  {broadcast && <StatusBadge status={broadcast.status || ""} />}
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-text-secondary">
                  <span>
                    Template:{" "}
                    <span className="font-medium text-brand-secondary">
                      {broadcast?.templateName || "-"}
                    </span>
                  </span>

                  <span className="hidden sm:inline">•</span>

                  <span>
                    Category:{" "}
                    <span className="font-medium text-brand-secondary">
                      {broadcast?.category || "-"}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-500">
            {error}
          </div>
        )}

        {!loading && broadcast && (
          <div className="space-y-6">
            <div>
              <div className="mb-3">
                <h2 className="text-base font-bold text-brand-secondary">
                  Overview
                </h2>
                <p className="text-xs text-text-secondary">
                  Summary of this broadcast
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
                <StatCard
                  label="Total"
                  value={broadcast.total}
                  icon={<FiUsers size={16} />}
                />

                <StatCard
                  label="Sent"
                  value={broadcast.sent}
                />

                <StatCard
                  label="Delivered"
                  value={broadcast.delivered}
                  icon={<FiCheckCircle size={16} />}
                />

                <StatCard
                  label="Read"
                  value={broadcast.read}
                />

                <StatCard
                  label="Failed"
                  value={broadcast.failed}
                  icon={<FiAlertCircle size={16} />}
                  variant="danger"
                />

                <StatCard
                  label="Cost"
                  value={`${broadcast.chargedAmount ?? 0}`}
                  icon={<FaIndianRupeeSign size={16} />}
                  variant="cost"
                />
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
              <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-background text-brand-main">
                    <FiUsers size={17} />
                  </div>

                  <div>
                    <h2 className="text-base font-bold text-brand-secondary">
                      Recipients
                    </h2>

                    <p className="text-xs text-text-secondary">
                      {recipientTotalItems} recipient
                      {recipientTotalItems === 1 ? "" : "s"} in this broadcast
                    </p>
                  </div>
                </div>
              </div>

              {recipientError && (
                <div className="px-5 pt-4 sm:px-6">
                  <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-500">
                    {recipientError}
                  </div>
                </div>
              )}

              <div
                className={`overflow-x-auto transition-opacity duration-300 ${
                  recipientLoading
                    ? "pointer-events-none opacity-40"
                    : "opacity-100"
                }`}
              >
                <table className="w-full min-w-[950px] text-sm">
                  <thead>
                    <tr className="border-b  font-bold border-gray-100 bg-gray-50/70 text-xs uppercase tracking-wide text-text-secondary">
                      <th className="px-5 py-3 text-left ">Mobile</th>

                      <th className="px-5 py-3 text-left">Name</th>

                      <th className="px-5 py-3 text-left">Variables Sent</th>

                      <th className="px-5 py-3 text-left">Status</th>

                      <th className="px-5 py-3 text-left">Delivery</th>

                      <th className="px-5 py-3 text-left">Error</th>
                    </tr>
                  </thead>

                  <tbody>
                    {recipients.length === 0 && !recipientLoading ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-14 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-background text-text-secondary">
                              <FiUsers size={18} />
                            </div>

                            <p className="text-sm font-medium text-brand-secondary">
                              No recipients found
                            </p>

                            <p className="mt-1 text-xs text-text-secondary">
                              There are no recipients to display for this
                              broadcast.
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      recipients.map((recipient) => (
                        <tr
                          key={recipient.id}
                          className="border-b border-gray-50 transition-colors last:border-0 hover:bg-gray-50/60"
                        >
                          <td className="px-5 py-4">
                            <span className="font-medium text-brand-secondary">
                              {recipient.mobile || "-"}
                            </span>
                          </td>

                          <td className="px-5 py-4 text-text-secondary">
                            {recipient.name || "-"}
                          </td>

                          <td className="px-5 py-4">
                            {recipient.bodyParams?.length ? (
                              <div className="flex max-w-[260px] flex-wrap gap-1.5">
                                {recipient?.bodyParams?.map((param, index) => (
                                  <span
                                    key={index}
                                    className="rounded-md border border-gray-100 bg-background px-2 py-1 text-xs font-medium text-brand-secondary"
                                  >
                                    {param}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-text-secondary">-</span>
                            )}
                          </td>

                          <td className="px-5 py-4">
                            <StatusShow status={recipient.status} />
                          </td>

                          <td className="px-5 py-4">
                            <StatusShow
                              status={recipient.deliveryStatus || ""}
                            />
                          </td>

                          <td className="max-w-[250px] px-5 py-4">
                            {recipient.errorMessage ? (
                              <span
                                className="block truncate cursor-pointer text-xs text-red-500"
                                title={recipient.errorMessage}
                              >
                                {recipient.errorMessage}
                              </span>
                            ) : (
                              <span className="text-text-secondary">-</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {recipientTotalItems > 0 && (
                <div className="flex items-center justify-end border-t border-gray-100 px-4 py-3 sm:px-6">
                  <Pagination
                    page={recipientPage}
                    setPage={setRecipientPage}
                    totalPages={recipientTotalPages}
                    totalItems={recipientTotalItems}
                    pageSize={recipientPageSize}
                    setPageSize={setRecipientPageSize}
                    loading={recipientLoading}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && !broadcast && !error && (
          <div className="rounded-xl border border-gray-100 bg-white py-14 text-center shadow-sm">
            <p className="text-sm font-medium text-brand-secondary">
              Broadcast not found
            </p>

            <p className="mt-1 text-xs text-text-secondary">
              The requested broadcast could not be found.
            </p>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

function StatCard({
  label,
  value,
  icon,
  variant = "default",
}: {
  label: string;
  value?: number | string | null;
  icon?: React.ReactNode;
  variant?: "default" | "danger" | "cost";
}) {
  const variantStyles = {
    default: {
      icon: "bg-background text-brand-main",
      value: "text-brand-secondary",
      line: "bg-brand-main",
    },
    danger: {
      icon: "bg-red-50 text-red-500",
      value: "text-red-500",
      line: "bg-red-400",
    },
    cost: {
      icon: "bg-green-50 text-green-600",
      value: "text-brand-secondary",
      line: "bg-green-500",
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className="group relative flex flex-col justify-between overflow-hidden rounded-xl border border-gray-100 bg-white px-4 py-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-start justify-between gap-2">
        <span className="text-md font-semibold uppercase text-text-secondary">
          {label}
        </span>

        {icon && (
          <div
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${styles.icon}`}
          >
            {icon}
          </div>
        )}
      </div>

      <div className={`mt-3 text-2xl font-bold tracking-tight ${styles.value}`}>
        {value ?? 0}
      </div>

      <div
        className={`absolute bottom-0 left-0 h-[2px] w-0 ${styles.line} transition-all duration-300 group-hover:w-full`}
      />
    </div>
  );
}

export default BroadcastDetails;